import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Bell, Heart, MessageSquare, Plus, Sparkles, Tag, Trash2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const LEVELS = ["İlköğretim", "Ortaöğretim", "Önlisans", "Lisans", "Yüksek lisans", "Doktora"];
export const HIGHER = LEVELS.slice(2);
const UNI_FIELDS = [["university", "Üniversite adı"], ["faculty", "Fakülte"], ["department", "Bölüm"]];
export const tagsOf = (i) => ({ theme_id: i?.theme_id || null, outcome_ids: i?.outcome_ids || [], grade: i?.grade || null,
  university: i?.university || "", faculty: i?.faculty || "", department: i?.department || "" });

let themeCache = null;
export const useThemes = (reload = 0) => {
  const [themes, setThemes] = useState(themeCache || []);
  useEffect(() => {
    api.get("/themes").then((r) => { themeCache = r.data; setThemes(r.data); }).catch(() => {});
  }, [reload]);
  return themes;
};

export const LikeButton = ({ kind, item, onChange }) => {
  const [s, setS] = useState({ liked: item.liked, n: item.like_count || 0 });
  useEffect(() => { setS({ liked: item.liked, n: item.like_count || 0 }); }, [item.liked, item.like_count]);
  const own = item.owned;
  const toggle = async (e) => {
    e.stopPropagation();
    if (own) return;
    try {
      const { data } = await api.post(`/likes/${kind}/${item.id}`);
      setS({ liked: data.liked, n: data.like_count });
      onChange?.();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <button onClick={toggle} title={own ? "Beğeni sayısı" : s.liked ? "Beğenmekten vazgeç" : "Beğen"}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold transition-[background-color,color,transform] duration-200 active:scale-90 ${s.liked ? "bg-pink-500/20 text-pink-300" : "bg-white/5 text-slate-300 hover:bg-white/10"} ${own ? "cursor-default" : ""}`}
      data-testid={`like-${kind}-${item.id}`}>
      <Heart className={`h-4 w-4 ${s.liked ? "fill-pink-400 text-pink-400" : ""}`} />{s.n}
    </button>
  );
};

export const TagChips = ({ item }) => {
  const themes = useThemes();
  const t = themes.find((x) => x.id === item.theme_id);
  if (!t && !item.grade) return null;
  const outs = t ? t.outcomes.filter((o) => item.outcome_ids?.includes(o.id)) : [];
  const uni = [item.university, item.faculty, item.department].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-wrap gap-1" data-testid={`tags-${item.id}`}>
      {item.grade && <span className="text-xs rounded-full bg-[#3B82F6]/20 text-[#93C5FD] px-2 py-0.5" data-testid={`tag-grade-${item.id}`}>{item.grade}</span>}
      {uni && <span className="text-xs rounded-full bg-white/5 text-slate-300 px-2 py-0.5 max-w-[18rem] truncate" title={uni} data-testid={`tag-uni-${item.id}`}>{uni}</span>}
      {t && <span className="inline-flex items-center gap-1 text-xs rounded-full bg-[#FACC15]/15 text-[#FDE68A] px-2 py-0.5"><Tag className="h-3 w-3" />{t.name}</span>}
      {outs.map((o) => <span key={o.id} className="text-xs rounded-full bg-white/5 text-slate-300 px-2 py-0.5 max-w-[14rem] truncate" title={o.text}>{o.text}</span>)}
    </div>
  );
};

export const TagPicker = ({ value, onChange }) => {
  const { teacher } = useAuth();
  const [n, setN] = useState(0);
  const [draft, setDraft] = useState("");
  const themes = useThemes(n);
  const theme = themes.find((t) => t.id === value.theme_id);
  const toggle = (id) => onChange({ ...value, outcome_ids: value.outcome_ids.includes(id) ? value.outcome_ids.filter((x) => x !== id) : [...value.outcome_ids, id] });
  const addOutcome = async () => {
    if (draft.trim().length < 2) return;
    try {
      const { data } = await api.put(`/themes/${theme.id}`, { name: theme.name, outcomes: [...theme.outcomes, { text: draft.trim() }] });
      const added = data.outcomes[data.outcomes.length - 1];
      setDraft("");
      setN((x) => x + 1);
      onChange({ ...value, outcome_ids: [...value.outcome_ids, added.id] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const higher = HIGHER.includes(value.grade);
  const setLevel = (v) => {
    const g = v === "none" ? null : v;
    onChange(HIGHER.includes(g) ? { ...value, grade: g, theme_id: null, outcome_ids: [] } : { ...value, grade: g, university: "", faculty: "", department: "" });
  };
  return (
    <div className="space-y-2" data-testid="tag-picker">
      <Label className="text-slate-300">Öğrenim düzeyi</Label>
      <Select value={value.grade || "none"} onValueChange={setLevel}>
        <SelectTrigger className="h-11" data-testid="tag-grade-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Seçilmedi</SelectItem>
          {LEVELS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
        </SelectContent>
      </Select>
      {higher && UNI_FIELDS.map(([k, l]) => (
        <Input key={k} value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} placeholder={l} maxLength={160} className="h-11" data-testid={`tag-${k}-input`} />
      ))}
      {!higher && value.grade && <>
      <Label className="text-slate-300">Tema ve öğrenme çıktıları</Label>
      <Select value={value.theme_id || "none"} onValueChange={(v) => onChange({ ...value, theme_id: v === "none" ? null : v, outcome_ids: [] })}>
        <SelectTrigger className="h-11" data-testid="tag-theme-select"><SelectValue placeholder="Tema seç" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Tema yok</SelectItem>
          {themes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {themes.length === 0 && <p className="text-xs text-slate-500">Henüz tema yok; yönetici Yönetim sayfasından ekleyebilir.</p>}
      {theme && theme.outcomes.map((o) => (
        <label key={o.id} className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5 cursor-pointer text-sm">
          <Checkbox checked={value.outcome_ids.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`tag-outcome-${o.id}`} className="mt-0.5" />
          <span>{o.text}</span>
        </label>
      ))}
      {theme && teacher?.role === "admin" && (
        <div className="flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Bu temaya yeni öğrenme çıktısı ekle" data-testid="tag-outcome-new"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOutcome(); } }} />
          <Button type="button" variant="outline" onClick={addOutcome} data-testid="tag-outcome-add"><Plus className="h-4 w-4" /></Button>
        </div>
      )}
      </>}
    </div>
  );
};

export const TagDialog = ({ kind, item, onOpenChange, onSaved }) => {
  const [tags, setTags] = useState(EMPTY_TAGS);
  useEffect(() => { if (item) setTags(tagsOf(item)); }, [item]);
  const save = async () => {
    try { await api.put(`/tags/${kind}/${item.id}`, tags); toast.success("Etiketler kaydedildi"); onSaved(); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tag-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Öğrenim düzeyi ve etiketler</DialogTitle></DialogHeader>
        <TagPicker value={tags} onChange={setTags} />
        <DialogFooter><Button onClick={save} className="font-bold" data-testid="tag-dialog-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EMPTY_TAGS = tagsOf(null);
export const saveTags = (kind, id, tags) => (tags.theme_id || tags.grade ? api.put(`/tags/${kind}/${id}`, tags) : Promise.resolve());

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const open = (o) => { if (o && data.unread) api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))); };
  const del = (id) => api.delete(`/notifications/${id}`).then(load);
  const clearRead = () => api.delete("/notifications/read").then(load);
  return (
    <Sheet onOpenChange={open}>
      <SheetTrigger asChild>
        <button className="relative p-2.5 rounded-xl border border-white/10 hover:bg-white/10" aria-label="Bildirimler" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-pink-500 text-white text-xs font-bold grid place-items-center" data-testid="notification-count">{data.unread}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-4 space-y-1" data-testid="notification-panel">
        <SheetHeader className="flex-row items-center justify-between space-y-0 pr-8 pb-2">
          <SheetTitle className="font-display text-2xl">Bildirimler</SheetTitle>
          {data.items.some((n) => n.read) && <Button size="sm" variant="ghost" className="text-red-300" onClick={clearRead} data-testid="notification-clear-read"><Trash2 className="h-4 w-4 mr-1" />Okunanları sil</Button>}
        </SheetHeader>
        {data.items.length === 0 && <p className="text-sm text-slate-400 px-2 py-4">Henüz bildirim yok.</p>}
        {data.items.map((n) => {
          const BADGE_NOTE = ["badge_request", "badge_granted", "badge_rejected"].includes(n.kind);
          const Icon = n.kind === "welcome" ? Sparkles : n.kind === "feedback_reply" ? MessageSquare : BADGE_NOTE ? BadgeCheck : Heart;
          const target = { pools: `/ogretmen/havuzlar/${n.item_id}`, activities: "/ogretmen/etkinlikler", rooms: "/ogretmen/oyunlar" }[n.item_kind];
          const click = n.kind === "welcome" ? () => window.dispatchEvent(new Event("fchem-feedback")) : BADGE_NOTE && target ? () => navigate(target) : undefined;
          return (
          <div key={n.id} onClick={click} className={`rounded-lg px-2 py-2 text-sm flex gap-2 ${click ? "cursor-pointer hover:bg-white/10" : ""} ${n.read ? "text-slate-400" : "bg-pink-500/10 text-white"}`} data-testid={`notification-${n.id}`}>
            <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${n.kind === "welcome" ? "text-[#FACC15]" : n.kind === "feedback_reply" ? "text-[#22C55E]" : BADGE_NOTE ? "text-sky-400" : "text-pink-400"}`} />
            <span className="flex-1">{n.message}<span className="block text-xs text-slate-500">{new Date(n.at).toLocaleString("tr-TR")}</span></span>
            {n.read && <button className="h-7 w-7 shrink-0 grid place-items-center rounded-md text-slate-500 hover:text-red-300 hover:bg-white/10" onClick={(e) => { e.stopPropagation(); del(n.id); }} aria-label="Bildirimi sil" data-testid={`notification-delete-${n.id}`}><X className="h-4 w-4" /></button>}
          </div>
          );
        })}
      </SheetContent>
    </Sheet>
  );
};

const ThemeEditor = ({ theme, onSaved }) => {
  const [name, setName] = useState(theme?.name || "");
  const [outs, setOuts] = useState(theme?.outcomes || []);
  const [draft, setDraft] = useState("");
  const save = async () => {
    const body = { name, outcomes: draft.trim() ? [...outs, { text: draft.trim() }] : outs };
    try {
      if (theme) await api.put(`/themes/${theme.id}`, body); else await api.post("/themes", body);
      toast.success("Tema kaydedildi");
      if (!theme) { setName(""); setOuts([]); }
      setDraft("");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/themes/${theme.id}`); toast.success("Tema silindi"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tid = theme?.id || "new";
  return (
    <div className="panel p-4 space-y-3" data-testid={`theme-editor-${tid}`}>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tema adı (örn. Atom ve Periyodik Sistem)" data-testid={`theme-name-${tid}`} />
        {theme && <Button variant="ghost" className="text-red-400" onClick={remove} data-testid={`theme-delete-${tid}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      {outs.map((o, i) => (
        <div key={o.id || i} className="flex gap-2">
          <Input value={o.text} onChange={(e) => setOuts(outs.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} data-testid={`theme-outcome-${tid}-${i}`} />
          <Button variant="ghost" onClick={() => setOuts(outs.filter((_, j) => j !== i))} data-testid={`theme-outcome-remove-${tid}-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Yeni öğrenme çıktısı (örn. 9.1.2.1 Atom modellerini karşılaştırır)" data-testid={`theme-outcome-new-${tid}`}
          onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { setOuts([...outs, { text: draft.trim() }]); setDraft(""); } }} />
        <Button variant="outline" onClick={() => { if (draft.trim()) { setOuts([...outs, { text: draft.trim() }]); setDraft(""); } }} data-testid={`theme-outcome-add-${tid}`}><Plus className="h-4 w-4" /></Button>
      </div>
      <Button onClick={save} disabled={name.trim().length < 2} className="font-bold" data-testid={`theme-save-${tid}`}>{theme ? "Kaydet" : "Tema ekle"}</Button>
    </div>
  );
};

export const ThemesPanel = () => {
  const [n, setN] = useState(0);
  const themes = useThemes(n);
  return (
    <div className="space-y-3" data-testid="themes-panel">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Tag className="h-6 w-6 text-[#FACC15]" />Temalar ve öğrenme çıktıları</h2>
      <p className="text-sm text-slate-400">Öğretmenler havuz, etkinlik, oyun ve oyun listelerini buradaki temalara ve öğrenme çıktılarına bağlar.</p>
      <div className="grid lg:grid-cols-2 gap-3">
        {themes.map((t) => <ThemeEditor key={`${t.id}-${t.outcomes.length}-${t.name}`} theme={t} onSaved={() => setN((x) => x + 1)} />)}
        <ThemeEditor key={`new-${n}`} onSaved={() => setN((x) => x + 1)} />
      </div>
    </div>
  );
};
